import { FileStatus } from '../enums/file-status.enum';

/**
 * Updates steps status and calculates progress.
 * @param steps Array of step objects.
 * @param progressIncrement Number representing the increment per step.
 * @returns Object with updated steps, actualStep index, and progress value.
 */
export function buildPartnerStatusProgress(
  steps: any[],
  progressIncrement: number,
): { steps: any[]; actualStep: number; progress: number } {
  // Check if all files in each section are OK for each step
  const initiated = steps[0].sections[0].stepsFlow[0].files[0].status !== FileStatus.PENDING;
  const hotelsDone = steps[0].sections[0].totalFiles === steps[0].sections[0].totalFilesOk;
  const rfFlowDone = steps[1].sections[0].totalFiles === steps[1].sections[0].totalFilesOk;
  const rfMgmtDone = steps[1].sections[1].totalFiles === steps[1].sections[1].totalFilesOk;
  const nrfFlowDone = steps[2].sections[0].totalFiles === steps[2].sections[0].totalFilesOk;
  const nrfMgmtDone = steps[2].sections[1].totalFiles === steps[2].sections[1].totalFilesOk;
  const cardFlowDone = steps[3].sections[0].totalFiles === steps[3].sections[0].totalFilesOk;
  const cardMgmtDone = steps[3].sections[1].totalFiles === steps[3].sections[1].totalFilesOk;

  // Array indicating which steps are completed
  const stepsDone = [
    initiated,
    hotelsDone,
    rfFlowDone && rfMgmtDone,
    nrfFlowDone && nrfMgmtDone,
    cardFlowDone && cardMgmtDone,
    false, // Confirm step is always last and not done until the end
  ];

  // Find the first step that is not completed
  const firstNotDoneIdx = stepsDone.findIndex((done) => !done) - 1;

  // Update 'active' and 'success' properties for each step
  const updatedSteps = steps.map((step, idx) => ({
    ...step,
    active: idx === firstNotDoneIdx,
    success: stepsDone[idx],
  }));

  // Calculate dynamic progress
  const progress = calculateDynamicProgress(updatedSteps, progressIncrement, firstNotDoneIdx);

  return {
    steps: updatedSteps,
    actualStep: firstNotDoneIdx,
    progress,
  };
}

/**
 * Calculates the current progress value for the progress bar.
 * @param steps Array of step objects.
 * @param progressIncrement Number representing the increment per step.
 * @param stepIdx Index of the current step.
 * @returns Progress value (number).
 */
export function calculateDynamicProgress(steps: any[], progressIncrement: number, stepIdx: number): number {
  // Each step occupies 2 increments (e.g. 12.5 * 2 = 25%)
  const baseProgress = progressIncrement * (stepIdx * 2);

  // Progress within current step (between stepIdx and stepIdx+1)
  let currentStepProgress = 0;
  if (stepIdx >= 0 && steps[stepIdx].sections) {
    const totalFiles = steps[stepIdx].sections
      .map((section: any) => Number(section.totalFiles || 0))
      .reduce((sum: number, val: number) => sum + val, 0);

    const totalFilesOk = steps[stepIdx].sections
      .map((section: any) => Number(section.totalFilesOk || 0))
      .reduce((sum: number, val: number) => sum + val, 0);

    if (totalFiles > 0) {
      currentStepProgress = progressIncrement * 2 * (totalFilesOk / totalFiles);
    }
  }
  return baseProgress + currentStepProgress;
}
